import React, { FC, useState } from 'react';
import type { ChatMessage } from '../../types';
import { parseFirestoreDate } from '../../types';
import './PackageRequestBubble.scss';

// SVG Icons imported from package-request
import { ReactComponent as GenderIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/gender.svg';
import { ReactComponent as FitnessGoalIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/fitness-goal.svg';
import { ReactComponent as FitnessLevelIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/fitness-level.svg';
import { ReactComponent as InjuriesIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/injuries.svg';
import { ReactComponent as FocusAreaIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/focus-area.svg';
import { ReactComponent as TrainingScheduleIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/training-schedule.svg';
import { ReactComponent as WorkoutDurationIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/workout-duration.svg';
import { ReactComponent as WorkoutLoadLevelIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/workout-load-level.svg';
import { ReactComponent as TrainingLocationIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/training-location.svg';
import { ReactComponent as SendOfferIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/send-offer.svg';
import { ReactComponent as InviteToFreeCallIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-request/invite-to-free-call.svg';

interface PackageRequestBubbleProps {
  message: ChatMessage;
  onSendOffer?: (message: ChatMessage) => void;
  onInviteFreeCall?: (message: ChatMessage) => void;
}

export const PackageRequestBubble: FC<PackageRequestBubbleProps> = ({
  message,
  onSendOffer,
  onInviteFreeCall,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const packageName = message.package_name || 'Basic';
  const currencySymbol = message.currency_symbol || (message.currency === 'GBP' ? '£' : '$');
  const price = message.price !== undefined ? message.price : 95;
  const duration = message.duration || '4 weeks';

  const sendDate = parseFirestoreDate(message.send_time);
  const formattedDate = sendDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const intake = message.intake_data || {};
  const gender = intake.gender || 'Female';
  const fitnessGoal = intake.fitness_goal || 'Build muscle';
  const fitnessLevel = intake.fitness_level || 'Beginner';
  const injuries = intake.injuries || 'Back, Knees';
  const focusAreas = intake.focus_areas || 'Full body';
  const trainingSchedule = intake.training_schedule || '3 per week';
  const workoutDuration = intake.workout_duration || '30 minutes';
  const workoutLoadLevel = intake.workout_load_level || 'Medium';
  const trainingLocation = intake.training_location || 'Gym';

  const startDate = message.start_date || '12.09.2026';
  const userMessage =
    message.body ||
    message.message ||
    'I want to build a consistent routine. Will you be able to help me?';

  return (
    <div className={`package-request-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header */}
      <div className="pkg-request-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="pkg-header-info">
          <h3 className="pkg-title">{`Request ${packageName} Package`}</h3>
          <div className="pkg-pricing-row">
            <span className="pkg-price">{`${currencySymbol}${price}`}</span>
            <span className="pkg-duration">{` / ${duration}`}</span>
          </div>
          <div className="pkg-meta-dates">
            <span>{`Requested ${formattedDate}`}</span>
          </div>
        </div>

        <button
          type="button"
          className="pkg-toggle-btn"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          <svg
            className={`chevron-icon ${isExpanded ? 'open' : ''}`}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      </div>

      {/* Expandable Questionnaire Body */}
      <div className={`pkg-request-body-wrapper ${isExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="pkg-request-body">
          <div className="pkg-questionnaire-list">
            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <GenderIcon />
                </span>
                <span className="field-label">{'Gender'}</span>
              </div>
              <span className="field-value">{gender}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <FitnessGoalIcon />
                </span>
                <span className="field-label">{'Fitness goal'}</span>
              </div>
              <span className="field-value">{fitnessGoal}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <FitnessLevelIcon />
                </span>
                <span className="field-label">{'Fitness level'}</span>
              </div>
              <span className="field-value">{fitnessLevel}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <InjuriesIcon />
                </span>
                <span className="field-label">{'Injuries'}</span>
              </div>
              <span className="field-value">{injuries}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <FocusAreaIcon />
                </span>
                <span className="field-label">{'Focus areas'}</span>
              </div>
              <span className="field-value">{focusAreas}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <TrainingScheduleIcon />
                </span>
                <span className="field-label">{'Training schedule'}</span>
              </div>
              <span className="field-value">{trainingSchedule}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <WorkoutDurationIcon />
                </span>
                <span className="field-label">{'Workout duration'}</span>
              </div>
              <span className="field-value">{workoutDuration}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <WorkoutLoadLevelIcon />
                </span>
                <span className="field-label">{'Workout load level'}</span>
              </div>
              <span className="field-value">{workoutLoadLevel}</span>
            </div>

            <div className="questionnaire-row">
              <div className="row-left">
                <span className="icon-wrap">
                  <TrainingLocationIcon />
                </span>
                <span className="field-label">{'Training location'}</span>
              </div>
              <span className="field-value">{trainingLocation}</span>
            </div>
          </div>

          {/* Start Date & Client Message */}
          <div className="pkg-additional-details">
            <div className="detail-line">
              <span className="detail-label">{'Start Date'}</span>
              <span className="detail-val">{startDate}</span>
            </div>

            <div className="detail-line">
              <span className="detail-label">{'Message'}</span>
              <p className="detail-msg">{userMessage}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pkg-request-actions">
        <button type="button" className="btn-send-offer" onClick={() => onSendOffer?.(message)}>
          <SendOfferIcon className="btn-icon" />
          <span>{'Send Offer'}</span>
        </button>

        <button
          type="button"
          className="btn-invite-free-call"
          onClick={() => onInviteFreeCall?.(message)}
        >
          <InviteToFreeCallIcon className="btn-icon" />
          <span>{'Invite to Free Call'}</span>
        </button>
      </div>
    </div>
  );
};
